import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  Receipt, FileDown, MapPin, Package, Calendar, Mail, Phone,
  Building2, AlertCircle, Loader2, BadgeCheck, Clock4,
} from "lucide-react";
import SEO from "../components/SEO";
import useDownloadPdf from "../hooks/useDownloadPdf";
import useMiscPageCms from "../hooks/useMiscPageCms";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SHARE_FALLBACK_CMS = {
  page_banner: {
    eyebrow: "Cargo with a smile",
    title: "Uw offerte van",
    highlight: "5999Cargo",
    subtitle:
      "Bekijk de details van uw offerte en download de PDF. Heeft u vragen of wilt u wijzigingen? Bel of mail ons direct.",
  },
  intro: {
    eyebrow: "Belangrijke informatie",
    title: "Deze offerte is een indicatie",
    body:
      "Definitieve tarieven worden bevestigd bij boeking. Heeft u vragen of wijzigingen? Reageer direct op de e-mail die u van ons heeft ontvangen of klik op Bel +31 (0) 488 23 5999 hierboven.",
  },
  cta_band: {},
};

/* ───────────────────────── helpers ───────────────────────── */

const fmtCurrency = (value, currency = "EUR") => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  try {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("nl-NL", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch {
    return String(iso).slice(0, 10);
  }
};

const STATUS_LABELS = {
  nieuw: { label: "Nieuw", tone: "bg-blue-50 text-blue-700 border-blue-200" },
  in_behandeling: { label: "In behandeling", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  wacht_klantinfo: { label: "Wachten op uw reactie", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  wacht_documenten: { label: "Wachten op documenten", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  wacht_betaling: { label: "Wachten op betaling", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  goedgekeurd: { label: "Goedgekeurd", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  afgewezen: { label: "Afgewezen", tone: "bg-rose-50 text-rose-700 border-rose-200" },
  verlopen: { label: "Verlopen", tone: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  omgezet: { label: "Omgezet naar boeking", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

/* ───────────────────────── page ───────────────────────── */

export default function PublicQuoteSharePage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, quote: null, error: null });
  const pdfDownloader = useDownloadPdf();
  const { data: cms } = useMiscPageCms("offerte-share", SHARE_FALLBACK_CMS);
  const cmsBanner = cms?.page_banner || SHARE_FALLBACK_CMS.page_banner;
  const cmsIntro = cms?.intro || SHARE_FALLBACK_CMS.intro;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/public/quotes/${encodeURIComponent(token)}`,
          { validateStatus: () => true },
        );
        if (cancelled) return;
        if (res.status === 200 && res.data?.quote) {
          setState({ loading: false, quote: res.data.quote, error: null });
        } else if (res.status === 410) {
          setState({ loading: false, quote: null, error: "revoked" });
        } else {
          setState({ loading: false, quote: null, error: "not_found" });
        }
      } catch (_err) {
        if (!cancelled) setState({ loading: false, quote: null, error: "network" });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const downloadPdf = () =>
    pdfDownloader.download(
      () =>
        axios
          .get(`${BACKEND_URL}/api/public/quotes/${encodeURIComponent(token)}/pdf`, {
            responseType: "blob",
            validateStatus: () => true,
          })
          .then(async (r) => {
            const ct = r.headers?.["content-type"] || "";
            if (r.status !== 200 || !ct.startsWith("application/pdf")) {
              // Build a friendly message from the JSON-error payload (if
              // any) and return a *rejected promise* rather than throwing
              // synchronously — that keeps React's dev error overlay from
              // catching this as an "uncaught" error. useDownloadPdf
              // catches the rejection and shows toast.error.
              let msg =
                "Offerte-PDF kon niet worden gedownload. Probeer het opnieuw of neem contact op.";
              try {
                const text = await r.data.text();
                const j = JSON.parse(text);
                if (j?.detail) msg = j.detail;
              } catch {
                /* keep default message */
              }
              return Promise.reject(new Error(msg));
            }
            let filename = `offerte-${(state.quote?.id || "").slice(0, 8) || "5999cargo"}.pdf`;
            const cd = r.headers?.["content-disposition"] || "";
            const m = cd.match(/filename="?([^";]+)"?/i);
            if (m) filename = m[1];
            return {
              blob: new Blob([r.data], { type: "application/pdf" }),
              filename,
            };
          }),
      {
        errorMessage:
          "Offerte-PDF kon niet worden gedownload. Probeer het opnieuw of neem contact op.",
        parseErrorMessage: (err) => err?.message || null,
      },
    );

  /* ─── loading state (header + skeleton already exposes test-ids) ─── */
  if (state.loading) {
    return (
      <div data-testid="quote-share-page" className="min-h-screen bg-[#f4f7fb] py-8 sm:py-12 px-4">
        <SEO title="Uw offerte van 5999Cargo" />
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 overflow-hidden">
            <div className="bg-[#082c50] px-6 sm:px-10 py-8 text-white">
              <div className="flex flex-col items-start gap-2">
                <div className="text-2xl sm:text-3xl font-extrabold tracking-tight">5999Cargo</div>
                <div className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-[#cfdcec]">{cmsBanner.eyebrow || "Cargo with a smile"}</div>
              </div>
            </div>
            <div className="px-6 sm:px-10 py-7 sm:py-9">
              <h1 className="text-[#082c50] text-2xl sm:text-3xl font-bold mb-1" data-testid="quote-share-title">
                {cmsBanner.title || "Uw offerte van 5999Cargo"} {cmsBanner.highlight ? <span className="text-[#1a5193]">{cmsBanner.highlight}</span> : null}
              </h1>
              <div
                data-testid="quote-share-loading"
                className="mt-4 inline-flex items-center gap-2 text-sm text-zinc-500"
              >
                <Loader2 className="h-4 w-4 animate-spin" /> Offerte wordt opgehaald…
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ─── revoked / not found ─── */
  if (state.error) {
    const isRevoked = state.error === "revoked";
    return (
      <div data-testid="quote-share-error" className="min-h-screen bg-[#f4f7fb] flex items-center justify-center px-4 py-12">
        <SEO title="Offerte niet beschikbaar — 5999Cargo" />
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-zinc-200 p-8 text-center">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-rose-50 text-rose-700 mb-4">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h1 className="text-xl font-bold text-[#082c50] mb-2">
            {isRevoked ? "Deze offerte-link is ingetrokken" : "Deze offerte-link is niet (meer) geldig"}
          </h1>
          <p className="text-sm text-zinc-600 mb-6">
            {isRevoked
              ? "De link die u hebt gebruikt is door 5999Cargo ingetrokken. Neem contact op om een nieuwe link te ontvangen."
              : "Mogelijk is de link verlopen of typ-fout. Neem contact op met 5999Cargo voor een nieuwe link."}
          </p>
          <div className="flex flex-col gap-2 items-stretch">
            <a
              href="tel:+31488235999"
              data-testid="quote-share-call-link"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#1a5193] text-white font-semibold hover:bg-[#194d8d] transition"
            >
              <Phone className="h-4 w-4" /> Bel +31 (0) 488 23 5999
            </a>
            <a
              href="mailto:info@5999cargo.nl"
              data-testid="quote-share-mail-link"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-[#1a5193] text-[#1a5193] font-semibold hover:bg-[#1a5193] hover:text-white transition"
            >
              <Mail className="h-4 w-4" /> Mail info@5999cargo.nl
            </a>
          </div>
        </div>
      </div>
    );
  }

  const q = state.quote || {};
  const fullName = [q.first_name, q.last_name].filter(Boolean).join(" ").trim() || q.name || "—";
  const status = STATUS_LABELS[q.status] || { label: q.status || "—", tone: "bg-zinc-100 text-zinc-700 border-zinc-200" };
  const currency = q.currency || "EUR";
  const shortId = (q.id || "").slice(0, 8).toUpperCase();
  const subtotal = q.subtotal ?? null;
  const vatPercent = q.vat_percent ?? null;
  const vatAmount = q.vat_amount ?? null;
  const total = q.total ?? q.price ?? q.amount ?? null;

  /* ─── happy path ─── */
  return (
    <div data-testid="quote-share-page" className="min-h-screen bg-[#f4f7fb] py-8 sm:py-12 px-4">
      <SEO
        title={`Uw offerte van 5999Cargo — ${shortId}`}
        description="Bekijk en download uw offerte van 5999Cargo Group Europe."
      />

      <div className="max-w-3xl mx-auto">
        {/* HERO CARD */}
        <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 overflow-hidden">
          <div className="bg-[#082c50] px-6 sm:px-10 py-8 text-white">
            <div className="flex flex-col items-start gap-2">
              <div className="text-2xl sm:text-3xl font-extrabold tracking-tight">5999Cargo</div>
              <div className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-[#cfdcec]">
                {cmsBanner.eyebrow || "Cargo with a smile"}
              </div>
            </div>
          </div>

          <div className="px-6 sm:px-10 py-7 sm:py-9 border-b border-zinc-100">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="text-[#082c50] text-2xl sm:text-3xl font-bold mb-1" data-testid="quote-share-title">
                  {cmsBanner.title || "Uw offerte van 5999Cargo"}{cmsBanner.highlight ? <> <span className="text-[#1a5193]">{cmsBanner.highlight}</span></> : null}
                </h1>
                <div className="text-sm text-zinc-600">
                  Offertenummer{" "}
                  <span className="font-semibold text-[#082c50]" data-testid="quote-share-number">
                    {shortId}
                  </span>
                  {q.quote_date || q.created_at ? (
                    <>
                      {" · "}{fmtDate(q.quote_date || q.created_at)}
                    </>
                  ) : null}
                </div>
                {cmsBanner.subtitle ? (
                  <p
                    className="mt-3 text-sm text-zinc-600 leading-relaxed max-w-xl"
                    data-testid="quote-share-subtitle"
                  >
                    {cmsBanner.subtitle}
                  </p>
                ) : null}
              </div>
              <span
                data-testid="quote-share-status"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${status.tone}`}
              >
                <BadgeCheck className="h-3.5 w-3.5" />
                {status.label}
              </span>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="quote-share-download-pdf"
                onClick={downloadPdf}
                disabled={pdfDownloader.loading}
                className="rounded-full bg-[#1a5193] hover:bg-[#194d8d] text-white font-semibold py-5 px-6"
              >
                {pdfDownloader.loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> PDF wordt gegenereerd…</>
                ) : (
                  <><FileDown className="h-4 w-4 mr-2" /> Download offerte als PDF</>
                )}
              </Button>
              <a
                href="tel:+31488235999"
                data-testid="quote-share-call-cta"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-[#082c50] text-[#082c50] font-semibold hover:bg-[#082c50] hover:text-white transition"
              >
                <Phone className="h-4 w-4" /> Bel +31 (0) 488 23 5999
              </a>
            </div>
            {pdfDownloader.isSlow && (
              <div data-testid="quote-share-slow-hint" className="mt-3 text-xs text-amber-700 inline-flex items-center gap-1.5">
                <Clock4 className="h-3.5 w-3.5" />
                {pdfDownloader.slowHint || "Het genereren duurt langer dan verwacht — een moment geduld a.u.b."}
              </div>
            )}
          </div>

          {/* SECTIONS */}
          <div className="px-6 sm:px-10 py-8 space-y-7">
            {/* Klant */}
            <Section icon={<Building2 className="h-4 w-4" />} title="Klant">
              <Row label="Naam" value={fullName} testid="quote-share-customer-name" />
              <Row label="Bedrijf" value={q.company_name} />
              {q.customer_type && (q.customer_type === "zakelijk" || q.customer_type === "particulier") && (
                <Row
                  label="Klanttype"
                  value={q.customer_type === "zakelijk" ? "Zakelijke klant" : "Particuliere klant"}
                  testid="quote-share-customer-type"
                />
              )}
              <Row label="E-mail" value={q.email} icon={<Mail className="h-3.5 w-3.5" />} />
              <Row label="Telefoon" value={q.phone} icon={<Phone className="h-3.5 w-3.5" />} />
            </Section>

            {/* Zending */}
            <Section icon={<Package className="h-4 w-4" />} title="Zending">
              <Row label="Bestemming" value={q.destination || q.destination_country} icon={<MapPin className="h-3.5 w-3.5" />} testid="quote-share-destination" />
              <Row label="Type zending" value={q.shipment_type || q.service} />
              <Row label="Verzendmethode" value={q.method} />
              {q.quantity != null && <Row label="Aantal" value={q.quantity} />}
              {q.boxes != null && <Row label="Aantal dozen" value={q.boxes} />}
              {(q.length_cm || q.width_cm || q.height_cm) && (
                <Row label="Afmetingen (L×B×H)"
                     value={[q.length_cm, q.width_cm, q.height_cm].filter(Boolean).join(" × ") + " cm"} />
              )}
              {q.weight_kg != null && <Row label="Werkelijk gewicht" value={`${q.weight_kg} kg`} />}
              {q.cbm != null && <Row label="Volume (CBM)" value={`${q.cbm} m³`} />}
              {q.volumetric_weight != null && <Row label="Volumetrisch gewicht" value={`${q.volumetric_weight} kg`} />}
              {q.chargeable_weight != null && <Row label="Belastbaar gewicht" value={`${q.chargeable_weight} kg`} />}
              {q.description && <Row label="Omschrijving" value={q.description} multiline />}
            </Section>

            {/* Prijs */}
            {(subtotal != null || total != null) && (
              <Section icon={<Receipt className="h-4 w-4" />} title="Prijsopbouw">
                <div className="rounded-xl border border-zinc-200 overflow-hidden">
                  {subtotal != null && (
                    <PriceRow label="Subtotaal" value={fmtCurrency(subtotal, currency)} />
                  )}
                  {vatPercent != null && vatAmount != null && (
                    <PriceRow label={`BTW (${vatPercent}%)`} value={fmtCurrency(vatAmount, currency)} />
                  )}
                  {total != null && (
                    <PriceRow
                      label="Totaal"
                      value={fmtCurrency(total, currency)}
                      strong
                      testid="quote-share-total"
                    />
                  )}
                </div>
              </Section>
            )}

            {/* Disclaimer */}
            <Section icon={<Calendar className="h-4 w-4" />} title={cmsIntro?.eyebrow || "Belangrijke informatie"}>
              <div className="text-sm text-zinc-600 leading-relaxed">
                {q.valid_until ? (
                  <p className="mb-2">
                    Deze offerte is geldig tot{" "}
                    <strong className="text-[#082c50]">{fmtDate(q.valid_until)}</strong>.
                  </p>
                ) : cmsIntro?.title ? (
                  <p className="mb-2 font-semibold text-[#082c50]">{cmsIntro.title}</p>
                ) : (
                  <p className="mb-2">
                    Deze offerte is een indicatie. Definitieve tarieven worden bevestigd bij boeking.
                  </p>
                )}
                <p data-testid="quote-share-disclaimer-body">
                  {cmsIntro?.body ||
                    "Heeft u vragen of wijzigingen? Reageer direct op de e-mail die u van ons heeft ontvangen of klik op Bel +31 (0) 488 23 5999 hierboven."}
                </p>
              </div>
            </Section>
          </div>

          {/* FOOTER */}
          <div className="border-t border-zinc-100 px-6 sm:px-10 py-6 bg-zinc-50/60 text-center text-xs text-zinc-500">
            <div className="font-semibold text-[#082c50] mb-1">5999Cargo Group Europe</div>
            <a href="https://5999cargo.nl" className="text-[#1a5193] hover:underline">5999cargo.nl</a>
            <p className="mt-3 leading-relaxed max-w-md mx-auto">
              Deze pagina is afgeschermd met een unieke link. Deel deze link alleen met
              personen die de offerte mogen inzien.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── primitives ───────────────────────── */

function Section({ icon, title, children }) {
  return (
    <section>
      <h2 className="text-xs uppercase tracking-[0.14em] font-bold text-[#1a5193] mb-3 flex items-center gap-2">
        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#1a5193]/10 text-[#1a5193]">
          {icon}
        </span>
        {title}
      </h2>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function Row({ label, value, multiline, icon, testid }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div
      data-testid={testid}
      className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-1.5 border-b border-zinc-50 last:border-b-0"
    >
      <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold sm:w-40 shrink-0 flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className={`text-sm text-[#082c50] ${multiline ? "whitespace-pre-wrap" : ""}`}>{value}</div>
    </div>
  );
}

function PriceRow({ label, value, strong, testid }) {
  return (
    <div
      data-testid={testid}
      className={`flex items-center justify-between px-4 py-3 ${
        strong ? "bg-[#082c50] text-white font-bold text-base" : "bg-zinc-50/50 text-zinc-700 border-b border-zinc-200"
      }`}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
